/* External */
import { useCallback, memo, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';

/* Hooks */
import useClickCallback from '../hooks/useClickCallback';
import useRefSync from '../hooks/useRefSync';
import { getDisplayName } from './utils';

export function withOnEnterPressAsMouseDownHandler(WrappedComponent) {
  const WithOnEnterPressAsClickHandler = forwardRef(
    ({ onEnterPress, ...props }, ref) => {
      const propsRef = useRefSync(props);
      const handleMouseDown = useClickCallback(useCallback((event) => {
        if (onEnterPress) {
          onEnterPress({ event, ...propsRef.current });
        }
      }, [onEnterPress, propsRef]));

      return (
        <WrappedComponent
          onEnterPress={onEnterPress}
          onMouseDown={handleMouseDown}
          ref={ref}
          {...props}
        />
      );
    },
  );

  WithOnEnterPressAsClickHandler.propTypes = {
    onEnterPress: PropTypes.func,
  };

  WithOnEnterPressAsClickHandler.defaultProps = {
    onEnterPress: undefined,
  };

  WithOnEnterPressAsClickHandler.displayName = `WithOnEnterPressAsClickHandler(${getDisplayName(WrappedComponent)})`;

  return memo(WithOnEnterPressAsClickHandler);
}

export default function withOnEnterPressAsMouseDownHandlerFocusable(options) {
  return compose(
    withOnEnterPressAsMouseDownHandler,
    withFocusable(options),
  );
}
